import { useEffect, useReducer, useState } from 'react';
import {
  Activity,
  ArrowRight,
  BrainCircuit,
  CalendarRange,
  ChartSpline,
  Check,
  ChevronRight,
  CircleDot,
  FlaskConical,
  Gauge,
  GlassWater,
  GitCompareArrows,
  History,
  Eye,
  Move,
  SlidersHorizontal,
  Pause,
  Play,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  TimerReset,
  X,
} from 'lucide-react';
import { demoReducer, createInitialState, type DemoState, type NavView } from './demo-state';
import { formatClock, harnesses, interventionHistory, patterns, sessions } from './demo-data';
import './styles.css';

const navItems: Array<{ id: NavView; label: string; icon: typeof Gauge }> = [
  { id: 'deck', label: 'Command deck', icon: Gauge },
  { id: 'perspectives', label: 'Perspectives', icon: ChartSpline },
  { id: 'sessions', label: 'Sessions', icon: History },
  { id: 'patterns', label: 'Patterns', icon: BrainCircuit },
  { id: 'lab', label: 'Intervention lab', icon: FlaskConical },
  { id: 'policy', label: 'Harness policy', icon: GitCompareArrows },
];

function Sidebar({ state, dispatch }: { state: DemoState; dispatch: React.Dispatch<Parameters<typeof demoReducer>[1]> }) {
  return (
    <aside className="sidebar">
      <div className="brand" aria-label="TWILL home">
        <span className="brand-mark"><CircleDot size={18} /></span>
        <span>TWILL</span>
      </div>
      <nav className="primary-nav" aria-label="Primary navigation">
        {navItems.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            className={state.view === id ? 'nav-item active' : 'nav-item'}
            onClick={() => dispatch({ type: 'navigate', view: id })}
            aria-label={label}
          >
            <Icon size={17} />
            <span>{label}</span>
          </button>
        ))}
      </nav>
      <div className="sidebar-foot">
        <div className="phase-rail" aria-label="Capability maturity">
          <span className="phase done">A</span><span className="phase-line done" />
          <span className="phase live">B</span><span className="phase-line" />
          <span className="phase">C</span>
        </div>
        <p><strong>Intervention mode</strong><span>Proposal + live demo</span></p>
      </div>
    </aside>
  );
}

function Topbar({ state, dispatch }: { state: DemoState; dispatch: React.Dispatch<Parameters<typeof demoReducer>[1]> }) {
  return (
    <header className="topbar">
      <div className="synthetic-badge"><ShieldCheck size={14} /> Fictional composite data</div>
      <div className="topbar-actions">
        <button className="quiet-button" onClick={() => dispatch({ type: 'reset' })}><RotateCcw size={15} /> Reset</button>
        <button className="primary-button tour-button" onClick={() => dispatch({ type: state.presentationStep ? 'presentation-next' : 'presentation-start' })}>
          <Sparkles size={15} /> {state.presentationStep ? `Story ${state.presentationStep}/5` : 'Present the story'}
        </button>
      </div>
    </header>
  );
}

function Timeline({ state, dispatch }: { state: DemoState; dispatch: React.Dispatch<Parameters<typeof demoReducer>[1]> }) {
  const progress = (state.hour / 14) * 100;
  return (
    <section className="timeline-section wellness-timeline" aria-labelledby="timeline-title">
      <div className="section-heading">
        <div><h2 id="timeline-title">Wellness across the workday</h2><p>Agent activity supplies context—it is never the wellness score</p></div>
        <div className="timeline-controls">
          <button className="icon-button" aria-label={state.playing ? 'Pause timeline' : 'Play timeline'} onClick={() => dispatch({ type: 'toggle-play' })}>
            {state.playing ? <Pause size={16} /> : <Play size={16} />}
          </button>
          <button className="signal-button" onClick={() => dispatch({ type: 'seek', hour: 13.2 })}>Jump to escalation <ArrowRight size={15} /></button>
        </div>
      </div>
      <div className="timeline-readout"><span>{formatClock(state.hour)}</span><span>{Math.round(progress)}% of scenario</span></div>
      <input
        className="timeline-range"
        type="range"
        min="0"
        max="14"
        step="0.1"
        value={state.hour}
        aria-label="Synthetic workday position"
        onChange={(event) => dispatch({ type: 'seek', hour: Number(event.target.value) })}
        style={{ '--timeline-progress': `${progress}%` } as React.CSSProperties}
      />
      <div className="timeline-hours" aria-hidden="true"><span>8:24 AM</span><span>12 PM</span><span>4 PM</span><span>8 PM</span><span>10:24 PM</span></div>
      <div className="wellness-lanes">
        <div className="wellness-lane"><span className="lane-label">Cognitive load</span><div className="wellness-track"><span className="track-fill load" style={{ width: `${Math.min(progress + 4, 100)}%` }} /><span className="threshold" /><span className="playhead" style={{ left: `${progress}%` }} /></div><strong className="danger">high</strong></div>
        <div className="wellness-lane"><span className="lane-label">Off-screen recovery</span><div className="wellness-track"><span className="recovery-event event-a" /><span className="recovery-event event-b missed" /><span className="recovery-event event-c missed" /><span className="playhead" style={{ left: `${progress}%` }} /></div><strong className="danger">132m</strong></div>
        <div className="wellness-lane"><span className="lane-label">Boundary integrity</span><div className="wellness-track"><span className="track-fill boundary" style={{ width: `${progress}%` }} /><span className="playhead" style={{ left: `${progress}%` }} /></div><strong>eroding</strong></div>
        <div className="wellness-lane context"><span className="lane-label">Agent intensity</span><div className="wellness-track">{sessions.map((session) => <button key={session.id} className={`session-block ${session.state}`} style={{ left: `${(session.start / 14) * 100}%`, width: `${Math.max((session.duration / 14) * 100, 3)}%` }} onClick={() => dispatch({ type: 'select-session', id: session.id })} aria-label={`Inspect ${session.title}`} title={session.title} />)}<span className="playhead" style={{ left: `${progress}%` }} /></div><strong>context</strong></div>
      </div>
    </section>
  );
}

function SignalStack({ state, dispatch }: { state: DemoState; dispatch: React.Dispatch<Parameters<typeof demoReducer>[1]> }) {
  return (
    <section className="signal-stack" aria-labelledby="signals-title">
      <div className="section-heading compact"><div><h2 id="signals-title">Wellness dimensions</h2><p>Observed signals + user feedback</p></div><button className="text-button" onClick={() => dispatch({ type: 'navigate', view: 'patterns' })}>Open graph <ChevronRight size={15} /></button></div>
      <div className="signal-rows">
        <button className="signal-row" onClick={() => dispatch({ type: 'select-pattern', id: 'cognitive-tunneling' })}>
          <span className="signal-glyph cyan"><BrainCircuit size={18} /></span>
          <span><strong>Cognitive load</strong><small>High-intensity window is still rising</small></span>
          <span className="signal-value danger">high</span>
        </button>
        <button className="signal-row" onClick={() => dispatch({ type: 'select-pattern', id: 'recovery-deferral' })}>
          <span className="signal-glyph amber"><Move size={18} /></span>
          <span><strong>Off-screen recovery</strong><small>132 minutes without a real pause</small></span>
          <span className="signal-value danger">overdue</span>
        </button>
        <button className="signal-row" onClick={() => dispatch({ type: 'select-pattern', id: 'boundary-erosion' })}>
          <span className="signal-glyph mint"><TimerReset size={18} /></span>
          <span><strong>Boundary integrity</strong><small>Planned stop moved twice</small></span>
          <span className="signal-value">eroding</span>
        </button>
        <button className="signal-row" onClick={() => dispatch({ type: 'select-pattern', id: 'cognitive-tunneling' })}>
          <span className="signal-glyph cyan"><Eye size={18} /></span>
          <span><strong>Sensory strain check</strong><small>Eyes, shoulders, food and water are unknown</small></span>
          <span className="signal-value mint-text">ask</span>
        </button>
      </div>
    </section>
  );
}

function HarnessStrip() {
  return (
    <section className="harness-strip" aria-label="Connected agent harnesses">
      {harnesses.map((harness) => (
        <div className="harness-item" key={harness.name}>
          <span className={`status-dot ${harness.status === 'stalled' ? 'stalled' : ''}`} />
          <span><strong>{harness.name}</strong><small>{harness.model}</small></span>
          <span className="harness-status">{harness.status}</span>
        </div>
      ))}
    </section>
  );
}

function CommandDeck({ state, dispatch }: { state: DemoState; dispatch: React.Dispatch<Parameters<typeof demoReducer>[1]> }) {
  return (
    <>
      <div className="page-intro">
        <div><p className="context-line"><Activity size={15} /> Personal wellness loop · Friday build cycle</p><h1>Developer wellness</h1><p>TWILL protects the human operating the agents—body, attention, agency and the right to stop.</p></div>
        <div className="operator-state"><span className={state.escalation === 'active' ? 'state-orb alert' : 'state-orb'} /><span><small>Wellness posture</small><strong>{state.escalation === 'active' ? 'Recovery intervention ready' : state.recoveryVisible ? 'Recovery reset observed' : 'Strain rising · recovery overdue'}</strong></span></div>
      </div>
      <HarnessStrip />
      <div className="deck-grid">
        <Timeline state={state} dispatch={dispatch} />
        <SignalStack state={state} dispatch={dispatch} />
      </div>
      <ResearchNote />
    </>
  );
}

function ResearchNote() {
  return (
    <aside className="research-note">
      <ShieldCheck size={18} />
      <p><strong>Wellness comes first.</strong> TWILL uses agent activity only to time support for attention, movement, nourishment, sensory recovery and boundaries. It does not infer a diagnosis or claim to know the body.</p>
      <span>4 wellness dimensions · user remains authority</span>
    </aside>
  );
}

const perspectiveLenses = [
  { id: 'time', label: 'By time', context: 'Time patterns' },
  { id: 'harness', label: 'By harness', context: 'Harness context' },
  { id: 'dimension', label: 'By dimension', context: 'Wellness dimensions' },
  { id: 'intervention', label: 'By intervention', context: 'Intervention outcomes' },
] as const;

const wellnessDimensions = ['All dimensions', 'Cognitive load', 'Off-screen recovery', 'Boundary integrity', 'Body check-ins'];
const recoveryResponses = [
  { label: 'Off-screen reset', helpful: 82, neutral: 12, harmful: 6 },
  { label: 'Close-the-day protocol', helpful: 76, neutral: 18, harmful: 6 },
  { label: 'Movement + water check', helpful: 68, neutral: 22, harmful: 10 },
  { label: 'Eyes + posture reset', helpful: 61, neutral: 31, harmful: 8 },
];

function PerspectivesView() {
  const [range, setRange] = useState<7 | 14 | 30>(14);
  const [lens, setLens] = useState<(typeof perspectiveLenses)[number]['id']>('time');
  const [dimension, setDimension] = useState(wellnessDimensions[0]);
  const lensContext = perspectiveLenses.find((item) => item.id === lens)?.context ?? 'Time patterns';
  const lensBias = perspectiveLenses.findIndex((item) => item.id === lens) * 7;
  const heatDays = range === 7 ? 7 : 14;

  return (
    <section className="view-page perspectives-page">
      <div className="page-intro perspective-intro">
        <div><p className="context-line"><ChartSpline size={15} /> Longitudinal wellness intelligence</p><h1>Wellness perspectives</h1><p>Slice the same evidence by time, harness context, wellness dimension and intervention response—without turning the developer into a productivity score.</p></div>
        <div className="perspective-meta"><span>{range}-day window</span><span>Lens: {lensContext}</span></div>
      </div>
      <div className="perspective-toolbar">
        <div className="filter-group" aria-label="Time window"><CalendarRange size={15} />{([7, 14, 30] as const).map((days) => <button key={days} aria-label={`${days} days`} className={range === days ? 'active' : ''} onClick={() => setRange(days)}>{days}D</button>)}</div>
        <div className="filter-group lens-group" aria-label="Perspective lens"><SlidersHorizontal size={15} />{perspectiveLenses.map((item) => <button key={item.id} className={lens === item.id ? 'active' : ''} onClick={() => setLens(item.id)}>{item.label}</button>)}</div>
        <div className="dimension-filter" aria-label="Wellness dimension">{wellnessDimensions.map((item) => <button key={item} className={dimension === item ? 'active' : ''} onClick={() => setDimension(item)}>{item}</button>)}</div>
      </div>

      <div className="perspective-summary" aria-label="Wellness perspective summary">
        <div><span className="summary-signal mint" /><span><small>Recovery kept</small><strong>{range === 30 ? '64%' : '71%'}</strong></span><em>+9 pts</em></div>
        <div><span className="summary-signal signal" /><span><small>Long strain windows</small><strong>{range === 7 ? '4' : range === 14 ? '7' : '16'}</strong></span><em>−2 vs prior</em></div>
        <div><span className="summary-signal cyan" /><span><small>Helpful interventions</small><strong>78%</strong></span><em>user-rated</em></div>
        <div><span className="summary-signal amber" /><span><small>Stop-time drift</small><strong>42m</strong></span><em>median</em></div>
      </div>

      <div className="perspective-grid" key={`${range}-${lens}-${dimension}`}>
        <section className="analytics-panel heatmap-panel">
          <header><div><h2>Wellness load map</h2><p>When sustained agent interaction coincides with deferred recovery</p></div><span className="panel-tag">{dimension}</span></header>
          <div className="heatmap-shell">
            <div className="heatmap-hours"><span>8a</span><span>11a</span><span>2p</span><span>5p</span><span>8p</span><span>11p</span></div>
            <div className="heatmap">
              {Array.from({ length: heatDays }, (_, day) => (
                <div className="heatmap-row" key={day}>
                  <span>{['M', 'T', 'W', 'T', 'F', 'S', 'S'][day % 7]}</span>
                  {Array.from({ length: 12 }, (_, slot) => {
                    const intensity = ((day * 17 + slot * 11 + lensBias + range) % 76) + (slot > 7 ? 20 : 4);
                    const level = intensity > 78 ? 'critical' : intensity > 57 ? 'high' : intensity > 34 ? 'medium' : 'low';
                    return <button key={slot} className={`heat-cell ${level}`} style={{ '--cell-delay': `${(day * 12 + slot) * 5}ms` } as React.CSSProperties} aria-label={`Day ${day + 1}, ${8 + slot}:00, ${level} wellness load`} title={`${level} wellness load`} />;
                  })}
                </div>
              ))}
            </div>
          </div>
          <footer><span><i className="legend low" /> restored</span><span><i className="legend medium" /> building</span><span><i className="legend high" /> strained</span><span><i className="legend critical" /> intervention</span></footer>
        </section>

        <section className="analytics-panel recovery-panel">
          <header><div><h2>Recovery response</h2><p>Direct user outcomes, never model-assumed</p></div><span className="panel-tag">{range} days</span></header>
          <div className="response-list">{recoveryResponses.map((response, index) => <div className="response-row" key={response.label}><div><strong>{response.label}</strong><span>{response.helpful}% helpful</span></div><div className="response-bar" aria-label={`${response.label}: ${response.helpful}% helpful, ${response.neutral}% neutral, ${response.harmful}% harmful`}><i className="helpful" style={{ width: `${response.helpful}%`, '--bar-delay': `${index * 70}ms` } as React.CSSProperties} /><i className="neutral" style={{ width: `${response.neutral}%` }} /><i className="harmful" style={{ width: `${response.harmful}%` }} /></div></div>)}</div>
          <div className="panel-insight"><Check size={15} /><p><strong>Movement beats generic breathing prompts.</strong> The user retained movement-first resets and suppressed breathing-only prompts during high-focus work.</p></div>
        </section>

        <section className="analytics-panel boundary-panel">
          <header><div><h2>Boundary drift</h2><p>Difference between intended and actual stop time</p></div><span className="panel-tag amber-text">median +42m</span></header>
          <svg className="boundary-chart" viewBox="0 0 640 190" role="img" aria-label="Boundary drift decreased over fourteen days">
            <g className="chart-grid"><line x1="38" y1="35" x2="620" y2="35"/><line x1="38" y1="88" x2="620" y2="88"/><line x1="38" y1="141" x2="620" y2="141"/></g>
            <line className="target-line" x1="38" y1="141" x2="620" y2="141" />
            <polyline className="drift-line" points="38,45 83,56 128,48 173,73 218,62 263,91 308,82 353,108 398,96 443,121 488,111 533,134 578,126 620,146" />
            <polyline className="recovery-line" points="38,154 83,148 128,151 173,139 218,142 263,125 308,129 353,111 398,118 443,93 488,101 533,79 578,83 620,63" />
          </svg>
          <div className="chart-legend"><span><i className="signal-line" /> stop-time drift</span><span><i className="mint-line" /> recovery kept</span><span><i className="target" /> chosen boundary</span></div>
        </section>

        <section className="analytics-panel harness-panel">
          <header><div><h2>Harness context</h2><p>Where patterns surface—not a ranking of tools</p></div><span className="panel-tag">context only</span></header>
          <div className="harness-matrix">
            <div className="matrix-head"><span>Harness</span><span>Load</span><span>Recovery kept</span><span>Late starts</span></div>
            {harnesses.map((harness, index) => <div className="matrix-row" key={harness.name}><span><i className={`matrix-dot dot-${index}`} />{harness.name}</span><span><b style={{ width: `${harness.load}%` }} /></span><strong>{[62, 79, 71, 68][index]}%</strong><em>{[5, 2, 3, 4][index]}</em></div>)}
          </div>
        </section>

        <section className="analytics-panel fit-panel">
          <header><div><h2>Intervention fit</h2><p>Which support works in which moment</p></div><span className="panel-tag">prospective</span></header>
          <div className="fit-table">
            <div className="fit-head"><span>Context</span><span>Best fit</span><span>Confidence</span></div>
            <div><span>After 110m continuous</span><strong>Off-screen reset</strong><em>high</em></div>
            <div><span>Stop time moved twice</span><strong>Close-the-day</strong><em>high</em></div>
            <div><span>Deployment in flight</span><strong>Wait for safe edge</strong><em>medium</em></div>
            <div><span>Body state unknown</span><strong>Ask, don’t infer</strong><em className="mint-text">required</em></div>
          </div>
        </section>

        <aside className="analytics-panel perspective-narrative">
          <span className="narrative-icon"><BrainCircuit size={18} /></span>
          <div><h2>What changed</h2><p>Recovery is increasingly kept when TWILL intervenes at a natural agent boundary rather than on a fixed timer. Late-night boundary drift remains the largest unresolved wellness cost.</p><button className="text-button">Open supporting patterns <ChevronRight size={15} /></button></div>
        </aside>
      </div>
    </section>
  );
}

function SessionsView({ dispatch }: { dispatch: React.Dispatch<Parameters<typeof demoReducer>[1]> }) {
  return (
    <section className="view-page">
      <div className="page-intro"><div><p className="context-line"><History size={15} /> Cross-harness activity</p><h1>Sessions</h1><p>One normalized stream; source boundaries remain visible.</p></div></div>
      <div className="session-table" role="table" aria-label="Synthetic agent sessions">
        <div className="session-table-head" role="row"><span>Session</span><span>Harness</span><span>Turns</span><span>Verified</span><span>State</span></div>
        {sessions.map((session) => (
          <button className="session-table-row" role="row" key={session.id} onClick={() => dispatch({ type: 'select-session', id: session.id })}>
            <span><strong>{session.title}</strong><small>Fictional composite · {formatClock(session.start)}</small></span>
            <span>{session.harness}</span><span className="tabular">{session.turns}</span><span className="tabular">{session.verification}%</span><span className={`table-state ${session.state}`}>{session.state}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function PatternsView({ dispatch }: { dispatch: React.Dispatch<Parameters<typeof demoReducer>[1]> }) {
  return (
    <section className="view-page">
      <div className="page-intro"><div><p className="context-line"><BrainCircuit size={15} /> Longitudinal model</p><h1>Pattern graph</h1><p>Claims stay scoped to evidence and keep their counterexamples.</p></div></div>
      <div className="pattern-layout">
        <div className="pattern-map" aria-label="Pattern relationship map">
          <span className="map-node core">Operator</span><span className="map-line line-a" /><span className="map-line line-b" /><span className="map-line line-c" />
          {patterns.map((pattern, index) => <button key={pattern.id} className={`map-node pattern node-${index + 1}`} onClick={() => dispatch({ type: 'select-pattern', id: pattern.id })}>{pattern.title}<small>{pattern.strength}% support</small></button>)}
        </div>
        <div className="pattern-list">
          {patterns.map((pattern) => (
            <article className="pattern-entry" key={pattern.id}>
              <header><span><strong>{pattern.title}</strong><small>{pattern.direction} confidence</small></span><span className="pattern-score">{pattern.strength}</span></header>
              <p>{pattern.summary}</p>
              <button className="text-button" aria-label={`Inspect ${pattern.title}`} onClick={() => dispatch({ type: 'select-pattern', id: pattern.id })}>Inspect evidence <ChevronRight size={15} /></button>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function LabView() {
  return (
    <section className="view-page">
      <div className="page-intro"><div><p className="context-line"><FlaskConical size={15} /> Outcome learning</p><h1>Intervention lab</h1><p>What helped, what did not, and what TWILL learned from the distinction.</p></div></div>
      <div className="lab-summary"><div><strong>7-minute off-screen reset</strong><p>Highest expected wellness value after sustained interaction and repeated boundary extension.</p></div><div><small>Expected benefit</small><strong className="mint-text">+31%</strong><span>self-rated recovery</span></div><div><small>Interruption cost</small><strong>7 min</strong><span>user-adjustable</span></div></div>
      <div className="history-list">
        <div className="history-head"><span>Intervention</span><span>Response</span><span>Observed effect</span></div>
        {interventionHistory.map((item) => <div className="history-row" key={item.when}><span><strong>{item.action}</strong><small>{item.when}</small></span><span className={item.response === 'Helpful' ? 'mint-text' : ''}>{item.response}</span><span>{item.effect}</span></div>)}
      </div>
    </section>
  );
}

function PolicyView({ state, dispatch }: { state: DemoState; dispatch: React.Dispatch<Parameters<typeof demoReducer>[1]> }) {
  return (
    <section className="view-page">
      <div className="page-intro"><div><p className="context-line"><GitCompareArrows size={15} /> Reversible adaptation · proposal only</p><h1>Harness policy</h1><p>One cross-harness rule, generated from outcomes and gated by the operator.</p></div><div className="confidence-block"><small>Proposal confidence</small><strong>{state.policyConfidence}%</strong></div></div>
      {state.lastResponse === 'accept' && <div className="learning-banner"><Check size={16} /><span><strong>Outcome recorded.</strong> Confidence moved to {state.policyConfidence}% after the accepted reset improved self-rated recovery.</span></div>}
      <div className="policy-layout">
        <div className="policy-rationale">
          <h2>Recovery boundary</h2><p>When continuous interaction exceeds 110 minutes and a chosen stop time has moved, propose an off-screen recovery reset before another agent starts.</p>
          <dl><div><dt>Applies to</dt><dd>Any connected agent harness</dd></div><div><dt>Wellness move</dt><dd>Move · water/food check · eyes away · breathe</dd></div><div><dt>Authority</dt><dd>Ask every time</dd></div><div><dt>Rollback</dt><dd>Automatic after 7 days without benefit</dd></div></dl>
        </div>
        <div className="diff-panel" aria-label="Proposed harness policy diff">
          <div className="diff-head"><span>twill.policy.yaml</span><span>v12 → v13 · reversible</span></div>
          <pre><span className="dim">@@ wellness_boundary</span>{'\n'}<span className="remove">- enabled: false</span>{'\n'}<span className="add">+ mode: propose</span>{'\n'}<span className="add">+ when:</span>{'\n'}<span className="add">+   continuous_minutes: &gt;= 110</span>{'\n'}<span className="add">+   stop_time_extended: true</span>{'\n'}<span className="add">+ action: off_screen_reset_7m</span>{'\n'}<span className="add">+ include: [move, water_food, eyes]</span>{'\n'}<span className="add">+ approval: operator_required</span></pre>
          <div className="diff-actions"><button className="quiet-button">Keep in proposal</button><button className={state.policyApplied ? 'success-button' : 'primary-button'} onClick={() => dispatch({ type: 'toggle-policy' })}>{state.policyApplied ? <><Check size={15} /> Canary scheduled</> : 'Approve 7-day canary'}</button></div>
        </div>
      </div>
    </section>
  );
}

function EvidenceDrawer({ state, dispatch }: { state: DemoState; dispatch: React.Dispatch<Parameters<typeof demoReducer>[1]> }) {
  const session = sessions.find((item) => item.id === state.selectedSessionId);
  const pattern = patterns.find((item) => item.id === state.selectedPatternId) ?? patterns[0];
  return (
    <aside className={state.evidenceOpen ? 'evidence-drawer open' : 'evidence-drawer'} aria-hidden={!state.evidenceOpen}>
      <div className="drawer-head"><span><small>Grounded claim</small><h2>Evidence inspector</h2></span><button className="icon-button" aria-label="Close evidence inspector" onClick={() => dispatch({ type: 'set-evidence', open: false })}><X size={17} /></button></div>
      {session ? <><div className="evidence-title"><span className="source-pill">{session.harness}</span><h3>{session.title}</h3><p>Selected synthetic session</p></div><div className="trace-block"><span>SYNTHETIC TRACE · {formatClock(session.start + 0.6)}</span><p>Interaction continued through another low-cost pause window. TWILL marks the timing pattern only; it does not claim what the developer felt.</p></div></> : <><div className="evidence-title"><span className="source-pill">WELLNESS PATTERN</span><h3>{pattern.title}</h3><p>{pattern.summary}</p></div><div className="evidence-list"><h3>Supporting</h3>{pattern.support.map((item) => <p key={item}><Check size={15} /> {item}</p>)}</div><div className="counter-evidence"><h3>Competing explanation</h3><p>{pattern.counter}</p></div><div className="trace-block"><span>SYNTHETIC TRACE · COMPOSITE</span><p>Continuous interaction and repeated boundary changes are visible. Body state remains unknown until the developer reports it.</p></div></>}
      <div className="drawer-foot"><button className="quiet-button">Mark inaccurate</button><button className="primary-button" onClick={() => dispatch({ type: 'set-evidence', open: false })}>Evidence holds</button></div>
    </aside>
  );
}

function InterventionOverlay({ dispatch }: { dispatch: React.Dispatch<Parameters<typeof demoReducer>[1]> }) {
  return (
    <div className="intervention-backdrop" role="presentation">
      <section className="intervention-panel" role="dialog" aria-modal="true" aria-labelledby="intervention-title">
        <header><span className="intervention-icon"><Move size={22} /></span><span><small>TWILL · WELLNESS ESCALATION</small><h2 id="intervention-title">Recovery has been deferred for 132 minutes</h2></span></header>
        <p className="intervention-copy">The work can keep generating; your recovery cannot be delegated. Continuous interaction is high, your planned stop moved twice, and no off-screen pause is visible. TWILL does not know how your body feels—check it now.</p>
        <div className="intervention-evidence"><span><TimerReset size={16} /> Continuous input <strong>132 min</strong></span><span><BrainCircuit size={16} /> Cognitive load <strong>high</strong></span><span><GlassWater size={16} /> Body signals <strong>unknown</strong></span></div>
        <div className="intervention-plan"><strong>7-minute recovery protocol</strong><p>Leave the screen. Drink water and check whether you need food. Focus your eyes far away, move for three minutes, release shoulders and jaw, then decide whether continuing still serves you.</p></div>
        <footer><button className="quiet-button" onClick={() => dispatch({ type: 'respond', response: 'dismiss' })}>Not useful</button><button className="quiet-button" onClick={() => dispatch({ type: 'respond', response: 'snooze' })}>Snooze 20 min</button><button className="primary-button" onClick={() => dispatch({ type: 'respond', response: 'accept' })}>Take 7-minute reset <ArrowRight size={15} /></button></footer>
      </section>
    </div>
  );
}

function MobileNav({ state, dispatch }: { state: DemoState; dispatch: React.Dispatch<Parameters<typeof demoReducer>[1]> }) {
  return <nav className="mobile-nav" aria-label="Mobile navigation">{navItems.slice(0, 5).map(({ id, label, icon: Icon }) => <button key={id} className={state.view === id ? 'active' : ''} aria-label={label} onClick={() => dispatch({ type: 'navigate', view: id })}><Icon size={19} /><span>{label.split(' ')[0]}</span></button>)}</nav>;
}

export default function App() {
  const [state, dispatch] = useReducer(demoReducer, undefined, createInitialState);

  useEffect(() => {
    if (!state.playing) return;
    const timer = window.setInterval(() => dispatch({ type: 'tick', amount: 0.18 }), 240);
    return () => window.clearInterval(timer);
  }, [state.playing]);

  return (
    <div className="app-shell">
      <Sidebar state={state} dispatch={dispatch} />
      <main className="main-shell">
        <Topbar state={state} dispatch={dispatch} />
        <div className="content-shell">
          {state.view === 'deck' && <CommandDeck state={state} dispatch={dispatch} />}
          {state.view === 'perspectives' && <PerspectivesView />}
          {state.view === 'sessions' && <SessionsView dispatch={dispatch} />}
          {state.view === 'patterns' && <PatternsView dispatch={dispatch} />}
          {state.view === 'lab' && <LabView />}
          {state.view === 'policy' && <PolicyView state={state} dispatch={dispatch} />}
        </div>
      </main>
      <EvidenceDrawer state={state} dispatch={dispatch} />
      {state.escalation === 'active' && <InterventionOverlay dispatch={dispatch} />}
      <MobileNav state={state} dispatch={dispatch} />
    </div>
  );
}
